import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import {
  materialiseCriteria,
  evaluateDataOverlay,
  applySlotOptions
} from './evaluate';
import { criteriaSentence, familyOptions } from './vocabulary';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Refining a data overlay (TGCLOUD-1670).
//
// The grammar is CascadeScreeningButton's, deliberately: a clinician who has
// used cascade screening already knows this popover. Parameter controls, live
// counts beside every choice, one aria-live summary line, Apply and Cancel.
//
// Live counts are the point. "Overdue by 7 days" versus "overdue by 21 days" is
// not a question anyone can answer in the abstract; it is answerable the moment
// the popover says one picks four people and the other picks one. Every count is
// evaluated against the SAME payload the chart will use, so the number cannot
// disagree with what appears when Apply is pressed.

const SUMMARY_SX = {
  fontSize: 12,
  color: 'text.secondary',
  mt: 1.25,
  minHeight: 32
};

/** Preview a candidate parameter set without applying it. */
const countFor = (preset, params, evalArgs) => {
  try {
    const results = evaluateDataOverlay({
      preset,
      criteria: materialiseCriteria(preset, params),
      ...evalArgs
    });
    return applySlotOptions(results, evalArgs.slotOptions).count;
  } catch (_) {
    return null;
  }
};

const IntParam = ({ def, value, onChange, preset, params, evalArgs }) => {
  const t = useAutoTranslation();

  const bands = def.bands || [];
  const inBands = bands.includes(Number(value));

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
        {t(def.label)}
      </Typography>
      {bands.length > 0 && (
        <RadioGroup
          value={inBands ? String(value) : ''}
          onChange={(e) => onChange(Number(e.target.value))}
        >
          {bands.map((band) => {
            const n = countFor(
              preset,
              { ...params, [def.key]: band },
              evalArgs
            );
            return (
              <FormControlLabel
                key={band}
                value={String(band)}
                control={<Radio size="small" />}
                sx={{ my: -0.5 }}
                label={
                  <Typography sx={{ fontSize: 13 }}>
                    {band === 1
                      ? t('{{count}} day', { count: band })
                      : t('{{count}} days', { count: band })}{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      ({n == null ? '—' : n})
                    </Box>
                  </Typography>
                }
              />
            );
          })}
        </RadioGroup>
      )}
      <TextField
        size="small"
        type="number"
        value={value ?? ''}
        onChange={(e) =>
          onChange(e.target.value === '' ? null : Number(e.target.value))
        }
        slotProps={{
          // MUI 9: `inputProps` is deprecated and, more to the point here, no
          // longer reaches the native input, so the aria-label silently went
          // nowhere and the control had no accessible name.
          htmlInput: {
            min: def.min,
            max: def.max,
            'aria-label': t(def.label)
          }
        }}
        sx={{ mt: bands.length ? 0.5 : 0, width: 120 }}
      />
    </Box>
  );
};

const SelectParam = ({ def, value, onChange, options, multiple }) => {
  const t = useAutoTranslation();
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
        {t(def.label)}
      </Typography>
      <Select
        size="small"
        fullWidth
        multiple={multiple}
        value={multiple ? value || [] : value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        renderValue={(v) => {
          const list = multiple ? v : [v];
          if (!list || !list.length || list[0] === '' || list[0] == null) {
            return (
              <Typography
                component="span"
                sx={{ fontSize: 13, color: 'text.disabled' }}
              >
                {t('Choose')}
              </Typography>
            );
          }
          return list
            .map((x) => options.find((o) => o.value === x)?.label || String(x))
            .join(', ');
        }}
        inputProps={{ 'aria-label': t(def.label) }}
      >
        {options.length === 0 && (
          <MenuItem value="" disabled>
            {t('Nothing recorded in this family')}
          </MenuItem>
        )}
        {options.map((o) => (
          <MenuItem key={String(o.value)} value={o.value} sx={{ fontSize: 13 }}>
            {o.label}
            {o.observed && (
              <Chip
                size="small"
                label={t('observed')}
                sx={{ ml: 1, height: 18, fontSize: 10 }}
              />
            )}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

const DataOverlayCriteriaPopover = ({
  open,
  anchorEl,
  onClose,
  preset,
  params,
  slotOptions,
  onApply,
  facts,
  personIndex,
  asOf,
  fmtDate,
  memberRecords,
  diagnoses,
  accent
}) => {
  const t = useAutoTranslation();

  const [draft, setDraft] = React.useState(params || {});
  const [draftOptions, setDraftOptions] = React.useState(
    slotOptions || { countHidden: true, markEveryRendering: true }
  );

  // Reopening after a change elsewhere must show the live values, not whatever
  // was last typed and cancelled.
  React.useEffect(() => {
    if (!open) return;
    setDraft(params || {});
    setDraftOptions(
      slotOptions || { countHidden: true, markEveryRendering: true }
    );
  }, [open, params, slotOptions]);

  const evalArgs = React.useMemo(
    () => ({ facts, personIndex, asOf, fmtDate, slotOptions: draftOptions }),
    [facts, personIndex, asOf, fmtDate, draftOptions]
  );

  const preview = React.useMemo(() => {
    if (!preset) return null;
    try {
      const criteria = materialiseCriteria(preset, draft);
      const results = evaluateDataOverlay({
        preset,
        criteria,
        facts,
        personIndex,
        asOf,
        fmtDate
      });
      return { criteria, results: applySlotOptions(results, draftOptions) };
    } catch (_) {
      return null;
    }
  }, [preset, draft, facts, personIndex, asOf, fmtDate, draftOptions]);

  if (!preset) return null;

  const setParam = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const r = preview?.results;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ p: 2, width: 320 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {t(preset.title)}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
          {t(preset.description)}
        </Typography>

        {preview?.criteria && (
          <Typography
            sx={{
              fontSize: 11.5,
              mt: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              color: 'text.secondary'
            }}
          >
            {criteriaSentence(preview.criteria, t)}
          </Typography>
        )}

        {(preset.paramDefs || []).map((def) => {
          const value = draft[def.key] ?? def.default;
          if (def.type === 'int') {
            return (
              <IntParam
                key={def.key}
                def={def}
                value={value}
                onChange={(v) => setParam(def.key, v)}
                preset={preset}
                params={draft}
                evalArgs={evalArgs}
              />
            );
          }
          // A preset's own options are ours and are translated for display; a
          // family's options are its record data and are shown as recorded.
          const options = def.options
            ? def.options.map((o) => ({ ...o, label: t(o.label) }))
            : familyOptions(def.optionsSource, { memberRecords, diagnoses });
          return (
            <SelectParam
              key={def.key}
              def={def}
              value={value}
              onChange={(v) => setParam(def.key, v)}
              options={options}
              multiple={def.type === 'multiEnum'}
            />
          );
        })}

        <Divider sx={{ my: 1.5 }} />

        <FormControlLabel
          sx={{ display: 'flex', my: -0.5 }}
          control={
            <Checkbox
              size="small"
              checked={draftOptions.countHidden !== false}
              onChange={(e) =>
                setDraftOptions((o) => ({
                  ...o,
                  countHidden: e.target.checked
                }))
              }
            />
          }
          label={
            <Typography sx={{ fontSize: 13 }}>
              {t('Count hidden members')}
            </Typography>
          }
        />
        <FormControlLabel
          sx={{ display: 'flex', my: -0.5 }}
          control={
            <Checkbox
              size="small"
              checked={draftOptions.markEveryRendering !== false}
              onChange={(e) =>
                setDraftOptions((o) => ({
                  ...o,
                  markEveryRendering: e.target.checked
                }))
              }
            />
          }
          label={
            <Typography sx={{ fontSize: 13 }}>
              {t('Mark every rendering of a person')}
            </Typography>
          }
        />

        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
          {t('Hidden members are counted but not drawn.')}
        </Typography>

        <Typography sx={SUMMARY_SX} aria-live="polite">
          {r
            ? [
                r.rosterSize === 1
                  ? t('{{count}} of {{total}} family member matches', {
                      count: r.count,
                      total: r.rosterSize
                    })
                  : t('{{count}} of {{total}} family members match', {
                      count: r.count,
                      total: r.rosterSize
                    }),
                r.hiddenCount
                  ? t('{{count}} hidden', { count: r.hiddenCount })
                  : null,
                r.unresolvedCount
                  ? t('{{count}} unresolved', { count: r.unresolvedCount })
                  : null,
                t('as of {{date}}', {
                  date: fmtDate ? fmtDate(r.asOf) : r.asOf
                })
              ]
                .filter(Boolean)
                .join(' · ')
            : t('Records could not be read')}
        </Typography>

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}
        >
          <Button size="small" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{ backgroundColor: accent }}
            onClick={() => {
              onApply(draft, draftOptions);
              onClose();
            }}
          >
            {t('Apply')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default DataOverlayCriteriaPopover;
